import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Device } from "./Devices";
import { SectionLabel, RevealLines } from "../site/motion";

const Card = ({ s, i }) => (
  <article data-testid={`case-screen-${i}`} className={`shrink-0 snap-start ${s.device === "phone" ? "w-[250px] lg:w-[220px] xl:w-[230px]" : "w-[86vw] sm:w-[560px] lg:w-[620px]"}`}>
    <Device device={s.device} src={s.img} alt={s.t} />
    <div className="mt-6 flex items-baseline gap-3">
      <span className="font-mono text-[11px]" style={{ color: "var(--case)" }}>{String(i + 1).padStart(2, "0")}</span>
      <h3 className="font-display text-xl font-semibold text-cream">{s.t}</h3>
    </div>
    <ol className="mt-3 space-y-2">
      {s.notes.map((n, j) => (
        <li key={j} className="flex gap-3 font-body text-sm leading-snug text-cream/75">
          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-cream/25 font-mono text-[10px] text-cream/80">{j + 1}</span>
          {n}
        </li>
      ))}
    </ol>
  </article>
);

const Pinned = ({ screens }) => {
  const wrap = useRef(null);
  const track = useRef(null);
  const [dist, setDist] = useState(0);
  const { scrollYProgress } = useScroll({ target: wrap, offset: ["start start", "end end"] });
  const x = useTransform(scrollYProgress, (v) => -v * dist);
  useEffect(() => {
    const el = track.current;
    if (!el) return undefined;
    const measure = () => setDist(Math.max(0, el.scrollWidth - window.innerWidth + 80));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={wrap} className="relative hidden lg:block" style={{ height: `calc(100vh + ${dist}px)` }}>
      <div className="sticky top-0 flex h-screen items-center overflow-hidden pt-16">
        <motion.div ref={track} style={{ x }} className="flex items-start gap-14 pl-10 pr-10">
          {screens.map((s, i) => <Card key={s.t} s={s} i={i} />)}
        </motion.div>
      </div>
    </div>
  );
};

export const ScreenGallery = ({ c }) => (
  <section data-testid="case-screens" className="relative py-24 md:py-32">
    <div className="mx-auto max-w-[1440px] px-6 md:px-10">
      <SectionLabel n="(04)">Screens</SectionLabel>
      <div className="mt-6 flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <RevealLines inView as="h2" lines={c.screensTitle}
          className="font-display text-4xl font-semibold uppercase leading-[0.95] tracking-[-0.03em] text-cream sm:text-5xl lg:text-6xl" />
        <p className="max-w-xs font-mono text-[11px] uppercase tracking-[0.2em] text-cream/55">
          <span className="hidden lg:inline">Scroll to move through the screens →</span>
          <span className="lg:hidden">Swipe to see more screens →</span>
        </p>
      </div>
    </div>
    <Pinned screens={c.screens} />
    <div data-testid="case-screens-mobile" className="mt-12 flex snap-x snap-mandatory gap-8 overflow-x-auto px-6 pb-6 lg:hidden [scrollbar-width:none]">
      {c.screens.map((s, i) => <Card key={s.t} s={s} i={i} />)}
    </div>
    {c.screensNote && <p className="mx-auto mt-4 max-w-[1440px] px-6 font-body text-xs text-cream/55 md:px-10">{c.screensNote}</p>}
  </section>
);
